import React, { useState, useEffect } from 'react';
import { chineseTTS } from '../utils/textToSpeech';
import { useTranslationStore } from '../store/translationStore';

interface SpeakerButtonProps {
  text: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

const SpeakerButton: React.FC<SpeakerButtonProps> = ({
  text,
  className = '',
  size = 'md',
  disabled = false
}) => {
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [localIsSpeaking, setLocalIsSpeaking] = useState(false);
  const { isSpeaking, speakingText, setTTSState } = useTranslationStore();

  // Size configurations
  const sizeConfig = {
    sm: {
      button: 'text-sm',
      icon: 'text-lg'
    },
    md: {
      button: 'text-base',
      icon: 'text-xl'
    },
    lg: {
      button: 'text-lg',
      icon: 'text-2xl'
    }
  };

  const config = sizeConfig[size];

  // Check TTS availability on mount
  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const available = await chineseTTS.isChineseAvailable();
        setIsAvailable(available);
      } catch (error) {
        console.error('Error checking TTS availability:', error);
        setIsAvailable(false);
      }
    };

    checkAvailability();
  }, []);

  // Monitor speaking state for this specific text
  useEffect(() => {
    const isThisTextSpeaking = isSpeaking && speakingText === text;
    setLocalIsSpeaking(isThisTextSpeaking);
  }, [isSpeaking, speakingText, text]);

  // Handle click to play/stop
  const handleClick = async () => {
    if (disabled || !isAvailable || !text.trim()) {
      return;
    }

    try {
      // If this text is currently speaking, stop it
      if (localIsSpeaking) {
        chineseTTS.stop();
        setTTSState(false, null);
        return;
      }

      // If another text is speaking, stop it first
      if (isSpeaking) {
        chineseTTS.stop();
      }

      // Start speaking this text
      setTTSState(true, text);

      await chineseTTS.speak(text, {
        rate: 0.8, // Slightly slower for better pronunciation
        pitch: 1.0,
        volume: 1.0
      });

      // Clear speaking state when done
      setTTSState(false, null);
    } catch (error) {
      console.error('TTS Error:', error);
      setTTSState(false, null);

      // Show user-friendly error
      const errorMessage = error instanceof Error ? error.message : 'Speech synthesis failed';
      console.warn('TTS failed:', errorMessage);
    }
  };

  // Don't render if TTS is not supported or not available
  if (isAvailable === false) {
    return null;
  }

  // Show loading state while checking availability
  if (isAvailable === null) {
    return (
      <button
        className={`${config.button} inline-flex items-center justify-center text-gray-400 cursor-not-allowed ${className}`}
        disabled
        aria-label="Checking speech availability..."
      >
        <span className={`${config.icon} animate-pulse`}>⋯</span>
      </button>
    );
  }

  // Determine button state
  const isButtonDisabled = disabled || !text.trim();
  const isActive = localIsSpeaking;

  // Button styles based on state
  const getButtonStyles = () => {
    if (isButtonDisabled) {
      return 'text-gray-400 cursor-not-allowed';
    }

    if (isActive) {
      return 'text-black hover:text-gray-700 animate-pulse';
    }

    return 'text-black hover:text-gray-600 active:text-gray-800 transition-colors duration-150';
  };

  // Icon based on state - using volume-up.png for both states
  const getIconOpacity = () => {
    if (isActive) {
      return 'opacity-80'; // Higher opacity when speaking
    }
    return 'opacity-30'; // Default opacity when idle
  };

  // Tooltip text
  const getTooltipText = () => {
    if (isButtonDisabled) {
      return 'No text to speak';
    }
    if (isActive) {
      return 'Click to stop speaking';
    }
    return 'Click to hear pronunciation';
  };

  return (
    <button
      onClick={handleClick}
      disabled={isButtonDisabled}
      className={`
        ${config.button}
        inline-flex items-center justify-center
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-opacity-50
        ${getButtonStyles()}
        ${className}
      `}
      title={getTooltipText()}
      aria-label={getTooltipText()}
      type="button"
    >
      <img
        src="/volume-up.png"
        alt="Speaker"
        className={`${config.icon === 'text-lg' ? 'w-5 h-5' : config.icon === 'text-xl' ? 'w-6 h-6' : 'w-7 h-7'} select-none ${getIconOpacity()}`}
        style={{
          animation: isActive ? 'tts-pulse 1.5s ease-in-out infinite' : 'none'
        }}
      />
    </button>
  );
};

export default SpeakerButton;